import React, { useEffect, useState } from 'react';
import { Bell, X, Smartphone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import {
  dismissPushPrompt,
  enablePushNotifications,
  getPushCapabilities,
  resyncPushSubscription,
} from '../../utils/pushSubscribe';

// Once per tab: heal drift between the FE's SUBSCRIBED_KEY flag and the
// backend's push_subscriptions collection. Fixes the case where a user
// "activated notifications" but the backend record was never persisted
// (blocked request, DB reset, etc.) — the banner then thinks it's done
// and pushes never arrive. Silent no-op when the browser has no
// subscription to sync.
const RESYNC_ONCE_KEY = 'pcc.pushResync.done';

/**
 * One-time banner asking leaders / DG / admin to enable push notifications
 * for new RDV requests. Surfaces three branches:
 *
 *   • Browser supports + permission default → "Activer" button.
 *   • iOS Safari (not standalone) → install-as-PWA hint, no permission ask.
 *   • Browser denied / unsupported → silent.
 *
 * Dismissal is persisted in localStorage so subsequent navigations stay
 * quiet. Only re-appears if the user resets it in DevTools.
 */
const PushPermissionBanner = () => {
  const { isAuthenticated, activeRole } = useAuth();
  const [caps, setCaps] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Only meaningful for roles that receive RDV / alert push.
    if (!isAuthenticated || !['leader', 'dg', 'admin'].includes(activeRole)) {
      setCaps(null);
      return;
    }
    setCaps(getPushCapabilities());
    // Silent resync once per tab — safe when permission is already
    // granted; no-op otherwise. Runs even when the FE thinks it's
    // already subscribed, so the backend record is refreshed each tab.
    try {
      if (sessionStorage.getItem(RESYNC_ONCE_KEY) !== '1') {
        sessionStorage.setItem(RESYNC_ONCE_KEY, '1');
        resyncPushSubscription().catch(() => {});
      }
    } catch (_) { /* sessionStorage disabled — skip */ }
  }, [isAuthenticated, activeRole]);

  if (!caps) return null;

  // Already subscribed, dismissed, or browser refused → nothing to show.
  if (caps.subscribed) return null;
  if (caps.dismissed) return null;
  if (!caps.supported && !caps.iosNeedsPwa) return null;
  if (caps.permission === 'denied') return null;
  // Permission already 'granted' but we have no FE subscription marker —
  // try to subscribe silently then exit.
  if (caps.supported && caps.permission === 'granted' && !caps.subscribed) {
    // fire and forget — only logs if it fails
    enablePushNotifications().then((ok) => {
      if (ok) setCaps(getPushCapabilities());
    });
    return null;
  }

  const handleEnable = async () => {
    setBusy(true);
    const ok = await enablePushNotifications();
    setBusy(false);
    setCaps(getPushCapabilities());
    if (!ok) dismissPushPrompt();
  };

  const handleDismiss = () => {
    dismissPushPrompt();
    setCaps(getPushCapabilities());
  };

  // iOS Safari, not standalone → tell user to install PWA first.
  if (caps.iosNeedsPwa) {
    return (
      <div
        className="fixed left-1/2 -translate-x-1/2 w-[calc(100%-24px)] max-w-md z-50 rounded-2xl bg-[#272727] text-[#F2F1EB] shadow-xl px-4 py-3 flex items-start gap-3"
      style={{
        // Sit above the bottom navbar (h-16 ≈ 64px) + its safe-area
        // padding (max(env(safe-area-inset-bottom), 12px)) + a 16px
        // breathing gap. 72px matches the .pcc-page bottom-padding
        // convention defined in index.css. Works on visitor + member
        // + leader + dg navbars since they all use the same shell.
        bottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 16px)',
      }}
        data-testid="push-banner-ios"
        role="dialog"
      >
        <div className="p-1.5 rounded-lg bg-[#D7A072]/20 mt-0.5">
          <Smartphone size={18} className="text-[#D7A072]" />
        </div>
        <div className="flex-1 text-sm">
          <p className="font-semibold mb-0.5">Recevoir les notifications</p>
          <p className="text-gray-300 text-[12px] leading-snug">
            Sur iPhone, ajoutez l'app à votre écran d'accueil (Partage → "Sur
            l'écran d'accueil") puis ouvrez-la depuis l'icône.
          </p>
        </div>
        <button
          onClick={handleDismiss}
          className="p-1 -m-1 text-gray-400 hover:text-gray-200"
          aria-label="Fermer"
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  // Standard prompt — supported + permission default
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 w-[calc(100%-24px)] max-w-md z-50 rounded-2xl bg-[#272727] text-[#F2F1EB] shadow-xl px-4 py-3 flex items-start gap-3"
      style={{
        // Sit above the bottom navbar (h-16 ≈ 64px) + its safe-area
        // padding (max(env(safe-area-inset-bottom), 12px)) + a 16px
        // breathing gap. 72px matches the .pcc-page bottom-padding
        // convention defined in index.css. Works on visitor + member
        // + leader + dg navbars since they all use the same shell.
        bottom: 'calc(72px + env(safe-area-inset-bottom, 0px) + 16px)',
      }}
      data-testid="push-banner"
      role="dialog"
    >
      <div className="p-1.5 rounded-lg bg-[#9398DE]/20 mt-0.5">
        <Bell size={18} className="text-[#9398DE]" />
      </div>
      <div className="flex-1 text-sm">
        <p className="font-semibold mb-0.5">Recevoir les notifications de RDV</p>
        <p className="text-gray-300 text-[12px] leading-snug">
          Soyez prévenu dès qu'un membre demande un rendez-vous.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <button
            onClick={handleEnable}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-[#9398DE] hover:bg-[#7B80C9] text-white text-xs font-semibold disabled:opacity-50"
            data-testid="push-banner-enable"
          >
            {busy ? 'Activation…' : 'Activer'}
          </button>
          <button
            onClick={handleDismiss}
            className="px-3 py-1.5 rounded-lg text-gray-300 hover:text-white text-xs"
          >
            Plus tard
          </button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="p-1 -m-1 text-gray-400 hover:text-gray-200"
        aria-label="Fermer"
      >
        <X size={18} />
      </button>
    </div>
  );
};

export default PushPermissionBanner;
